import { withDependencies, named } from '@wix/thunderbolt-ioc'
import { ExperimentsSymbol, LoggerSymbol, PageFeatureConfigSymbol, FeatureStateSymbol, Structure } from '@wix/thunderbolt-symbols'
import type { Experiments, ILogger, IStructureStore } from '@wix/thunderbolt-symbols'
import type { IFeatureState } from 'thunderbolt-feature-state'
import {
	getByCompId as pureGetByCompId,
	getClosestByCompId as pureGetClosestByCompId,
	querySelector as pureQuerySelector,
	querySelectorAll as pureQuerySelectorAll,
	getElementCompId as pureGetElementCompId,
} from '@wix/thunderbolt-commons'
import type { IDomSelectors, DomSelectorsPageConfig, DomSelectorsState } from './types'
import { name } from './symbols'

const createDomSelectorsImpl = (
	experiments: Experiments,
	logger: ILogger,
	getComponentIds: () => Set<string> | undefined
): IDomSelectors => {
	return {
		getByCompId: (compId: string, doc?: Document, context?: Record<string, any>) => {
			return pureGetByCompId(compId, {
				experiments,
				logger,
				document: doc,
				context,
			}) as HTMLElement | null
		},

		getClosestByCompId: (element: Element, compId: string, context?: Record<string, any>) => {
			return pureGetClosestByCompId(element, compId, {
				experiments,
				logger,
				context,
			})
		},

		querySelector: (selector: string, doc?: Document, context?: Record<string, any>) => {
			return pureQuerySelector(selector, {
				experiments,
				logger,
				document: doc,
				context,
			})
		},

		querySelectorAll: (selector: string, doc?: Document, context?: Record<string, any>) => {
			return pureQuerySelectorAll(selector, {
				experiments,
				logger,
				document: doc,
				context,
			})
		},

		getElementCompId: (element: Element, context?: Record<string, any>) => {
			// Get component IDs dynamically - allows for reactive updates
			return pureGetElementCompId(element, { componentIds: getComponentIds(), experiments, context })
		},
	}
}

/**
 * Site-level binding using FeatureState to share component IDs across pages
 * Also subscribes to structure changes to capture component IDs as they're added
 */
export const DomSelectorsSite = withDependencies<IDomSelectors>(
	[ExperimentsSymbol, LoggerSymbol, named(FeatureStateSymbol, name), Structure] as const,
	(experiments, logger, featureState: IFeatureState<DomSelectorsState>, structureStore: IStructureStore) => {
		// Initialize state if not already done
		if (!featureState.get()) {
			featureState.update(() => ({ componentIds: new Set<string>() }))
		}

		const state = featureState.get()!

		// Subscribe to structure changes to capture component IDs as pages load
		structureStore.subscribeToChanges((partialStore) => {
			Object.keys(partialStore).forEach((compId) => {
				if (partialStore[compId]) {
					state.componentIds.add(compId)
				}
			})
		})

		return createDomSelectorsImpl(experiments, logger, () => state.componentIds)
	}
)

/**
 * Page-level binding that:
 * 1. Gets component IDs from CARMI page config
 * 2. Updates the site-level FeatureState so site binding can access them
 */
export const DomSelectorsPage = withDependencies<IDomSelectors>(
	[ExperimentsSymbol, LoggerSymbol, named(PageFeatureConfigSymbol, name), named(FeatureStateSymbol, name)] as const,
	(experiments, logger, pageConfig: DomSelectorsPageConfig, featureState: IFeatureState<DomSelectorsState>) => {
		// Get current state or initialize
		const currentState = featureState.get() || { componentIds: new Set<string>() }

		// Add component IDs from this page to the shared state
		if (pageConfig.allComponentIds) {
			pageConfig.allComponentIds.forEach((id) => currentState.componentIds.add(id))
		}

		// Update the feature state
		featureState.update(() => currentState)

		return createDomSelectorsImpl(experiments, logger, () => currentState.componentIds)
	}
)

/**
 * Editor-level binding that:
 * 1. Gets component IDs from CARMI page config
 * 2. Subscribes to structure changes to add new component IDs
 */
export const DomSelectorsEditor = withDependencies<IDomSelectors>(
	[ExperimentsSymbol, LoggerSymbol, named(PageFeatureConfigSymbol, name), named(FeatureStateSymbol, name), Structure] as const,
	(
		experiments,
		logger,
		pageConfig: DomSelectorsPageConfig,
		featureState: IFeatureState<DomSelectorsState>,
		structureStore: IStructureStore
	) => {
		// Get current state or initialize
		const currentState = featureState.get() || { componentIds: new Set<string>() }

		// Add component IDs from this page to the shared state
		if (pageConfig.allComponentIds) {
			pageConfig.allComponentIds.forEach((id) => currentState.componentIds.add(id))
		}

		// Update the feature state
		featureState.update(() => currentState)

		// Subscribe to structure changes to add new component IDs when components are added
		structureStore.subscribeToChanges((partialStore) => {
			Object.keys(partialStore).forEach((compId) => {
				if (partialStore[compId]) {
					currentState.componentIds.add(compId)
				}
			})
		})

		return createDomSelectorsImpl(experiments, logger, () => currentState.componentIds)
	}
)

// Default export for backward compatibility
export const DomSelectors = DomSelectorsPage
